import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight, FiHeart, FiStar } from 'react-icons/fi';
import { IoFlashOutline } from 'react-icons/io5';
import {getImageUrl} from '../../utils/imageUtils'

const RecommendedForYou = ({ 
  user, 
  listings = [], 
  userWishlist = [], 
  onListingPress,
  onSeeAllPress,
  title = "Recommended For You",
  subtitle = "Based on your interests and browsing history"
}) => {
  const navigate = useNavigate();
  
  // Enhanced personalized recommendations
  const getPersonalizedRecommendations = useMemo(() => {
    if (!listings || listings.length === 0) return [];
    
    // Filter out user's own listings first
    const filteredListings = listings.filter(listing => {
      return !(user && listing.seller_id === user.user_id);
    });
    
    if (filteredListings.length === 0) return [];
    
    const wishlistCategories = new Set();
    userWishlist.forEach(item => {
      if (item.category_id) {
        wishlistCategories.add(item.category_id);
      }
    });
    
    const scoredListings = filteredListings.map(listing => {
      let score = 0;
      let reasons = [];
      
      // 1. Boost boosted listings
      if (listing.is_boosted) {
        score += 3;
        reasons.push('Popular');
      }
      
      // 2. Location-based scoring
      if (user?.state_id && listing.state_id === user.state_id) {
        score += 2;
        reasons.push('Near you');
      }
      
      // 3. Wishlist-based scoring
      if (wishlistCategories.has(listing.category_id)) {
        score += 4;
        reasons.push('Matches your interests');
      }
      
      // 4. Recency scoring
      const daysAgo = Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60 * 24));
      if (daysAgo < 7) score += 2;
      if (daysAgo < 1) {
        score += 1;
        reasons.push('Just added');
      }
      
      return {
        ...listing,
        recommendationScore: score,
        recommendationReason: reasons[0] || 'Trending',
      };
    });
    
    // Filter out items with negative scores and sort
    return scoredListings
      .filter(item => item.recommendationScore > 0)
      .sort((a, b) => b.recommendationScore - a.recommendationScore)
      .slice(0, 6);
  }, [listings, user, userWishlist]);

  // Helper function to get popular listings for non-logged in users
  const getPopularListings = useMemo(() => {
    if (!listings || listings.length === 0) return [];
    
    // Filter out user's own listings even for non-logged in users
    // (Though for non-logged in users, this won't apply since user is null)
    return listings
      .filter(listing => !(user && listing.seller_id === user.user_id))
      .filter(listing => listing.is_boosted || Math.random() > 0.5)
      .slice(0, 6)
      .map(listing => ({
        ...listing,
        recommendationReason: listing.is_boosted ? 'Popular' : 'Trending'
      }));
  }, [listings, user]); // Added user to dependencies

  const displayListings = user ? getPersonalizedRecommendations : getPopularListings;

  const handleListingPress = (listing, index) => {
    if (onListingPress) {
      onListingPress(listing, index);
    } else {
      navigate(`/listing/${listing.listing_id}`);
    }
  };

  const handleSeeAllClick = () => {
    if (onSeeAllPress) {
      onSeeAllPress();
    } else {
      navigate('/recommendations', { 
        state: { 
          listings: displayListings,
          userWishlist,
          userId: user?.user_id || '0'
        }
      });
    }
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'Popular':
        return IoFlashOutline;
      case 'Trending':
        return FiStar;
      default:
        return FiStar;
    }
  };

  if (displayListings.length === 0) {
    return (
      <div className="px-4 mb-6 overflow-hidden">
        <div className="flex justify-between items-center mb-4 overflow-hidden">
          <div className="overflow-hidden">
            <h2 className="text-lg font-bold text-gray-900 truncate">{title}</h2>
            <p className="text-sm text-gray-600 truncate">
              {user ? "We're learning your preferences..." : "Popular items in your area"}
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-8 text-center shadow-sm overflow-hidden">
          <FiHeart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2 truncate">No recommendations yet</h3>
          <p className="text-gray-600 mb-4 truncate">
            {user ? "Start browsing and add items to your wishlist" : "Sign in to get personalized recommendations"}
          </p>
          {!user && (
            <button 
              onClick={() => navigate('/login')}
              className="bg-orange-500 text-white font-semibold py-2 px-6 rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 mb-6 overflow-hidden">
      <div className="flex justify-between items-center mb-4 overflow-hidden">
        <div className="overflow-hidden">
          <h2 className="text-lg font-bold text-gray-900 truncate">{title}</h2>
          <p className="text-sm text-gray-600 truncate">
            {user ? subtitle : "Popular items in your area"}
          </p>
        </div>
        
        <button 
          onClick={handleSeeAllClick}
          className="flex items-center text-orange-500 font-semibold text-sm hover:text-orange-600 whitespace-nowrap"
        >
          See All
          <FiChevronRight className="w-4 h-4 ml-1 flex-shrink-0" />
        </button>
      </div>

      {/* Horizontal Scrollable Recommendations - FIXED: Removed negative margins */}
      <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide">
      {displayListings.map((listing, index) => {
        const ReasonIcon = getReasonIcon(listing.recommendationReason || 'Trending');

        // SAFELY extract image URL — this is the fix
        const primaryImage = listing.images?.[0];
        const imageUrl = primaryImage?.image_url || listing.image_url || primaryImage?.url || listing.cover_image;
        const finalImageUrl = imageUrl ? getImageUrl(imageUrl) : 'https://via.placeholder.com/160x160?text=No+Image';

        return (
            <div 
            key={listing.listing_id || listing.id || index}
            onClick={() => handleListingPress(listing, index)}
            className="flex-shrink-0 w-40 bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md cursor-pointer transition-shadow"
            >
            <div className="relative h-32 bg-gray-100">
                <img 
                src={finalImageUrl}
                alt={listing.title || 'Product'}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/160x160?text=No+Image';
                    e.target.onerror = null; // prevent infinite loop
                }}
                />
                
                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-col space-y-1">
                {listing.is_boosted && (
                    <div className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded flex items-center space-x-1">
                    <IoFlashOutline className="w-3 h-3" />
                    
                    </div>
                )}
              
                </div>
            </div>

            <div className="p-3">
                <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2 h-10">
                {listing.title || 'Untitled Item'}
                </h3>
                
                <p className="text-orange-500 font-bold text-base mb-2">
                {(listing.currency === 'USD' ? '$' : '₦')}
                {parseFloat(listing.price || 0).toLocaleString()}
                </p>

                <div className="flex items-center space-x-1 bg-orange-50 text-orange-700 text-xs px-2 py-1 rounded w-fit">
                <ReasonIcon className="w-3 h-3" />
                <span>{listing.recommendationReason || 'Trending'}</span>
                </div>
            </div>
            </div>
        );
        })}
      </div>
    </div>
  );
};

export default RecommendedForYou;